import html2canvas from 'html2canvas';
import { toast } from 'sonner';

/**
 * Downloads a chart container as a full-resolution PNG.
 * Expands ALL scrollable / overflow-hidden containers before capture so that
 * content scrolled off-screen (horizontal bars, long agent lists, etc.) is
 * fully included in the exported image.
 *
 * @param {React.RefObject|HTMLElement} containerRefOrEl - React ref OR raw DOM element
 * @param {string}   filename   - base filename (YYYY-MM-DD is appended automatically)
 * @param {boolean}  isDark     - current theme for background colour
 * @param {function} setLoading - (bool) → void, toggles loading indicator
 */
export const downloadChartPNG = async (containerRefOrEl, filename, isDark, setLoading) => {
  // Accept both a React ref AND a raw DOM element (needed for heatmap)
  const container = containerRefOrEl?.current ?? containerRefOrEl;
  if (!container) return;

  setLoading(true);
  try {
    const PADDING = 24;

    // ── Step 1: expand ALL descendants that clip or scroll content ────────────
    // Check BOTH computed styles (Tailwind / CSS classes) AND inline styles
    // (e.g. the min-width wrappers used inside horizontal-scroll containers).
    const saved = [];

    const collectEl = (el) => {
      const cs  = window.getComputedStyle(el);
      const ovs = [
        cs.overflow,   cs.overflowX,   cs.overflowY,
        el.style.overflow, el.style.overflowX, el.style.overflowY,
      ].join('|');
      if (/auto|scroll|hidden/.test(ovs)) {
        saved.push({
          el,
          maxHeight:  el.style.maxHeight,
          maxWidth:   el.style.maxWidth,
          height:     el.style.height,
          overflow:   el.style.overflow,
          overflowX:  el.style.overflowX,
          overflowY:  el.style.overflowY,
        });
        el.style.maxHeight = 'none';
        el.style.maxWidth  = 'none';
        el.style.height    = 'auto';
        el.style.overflow  = 'visible';
        el.style.overflowX = 'visible';
        el.style.overflowY = 'visible';
      }
    };

    collectEl(container);
    container.querySelectorAll('*').forEach(collectEl);

    // ── Step 2: measure full rendered size AFTER expansion ────────────────────
    const rect     = container.getBoundingClientRect();
    const naturalW = Math.ceil(Math.max(container.scrollWidth,  rect.width,  400));
    const naturalH = Math.ceil(Math.max(container.scrollHeight, rect.height, 200));

    // ── Step 3: restore original styles immediately (only the clone stays expanded)
    saved.forEach(({ el, maxHeight, maxWidth, height, overflow, overflowX, overflowY }) => {
      el.style.maxHeight = maxHeight;
      el.style.maxWidth  = maxWidth;
      el.style.height    = height;
      el.style.overflow  = overflow;
      el.style.overflowX = overflowX;
      el.style.overflowY = overflowY;
    });

    // ── Step 4: render with html2canvas ───────────────────────────────────────
    const canvas = await html2canvas(container, {
      backgroundColor: isDark ? '#0f1729' : '#ffffff',
      scale:        2,        // 2× for crisp HiDPI output
      width:        naturalW,
      height:       naturalH,
      windowWidth:  naturalW + PADDING * 2,
      windowHeight: naturalH + PADDING * 2,
      x: 0, y: 0, scrollX: 0, scrollY: 0,
      useCORS: true, allowTaint: true, logging: false,
      onclone: (_doc, clonedEl) => {
        // Hide all Export/download buttons in the captured image
        clonedEl.querySelectorAll('[data-download-btn]').forEach((btn) => {
          btn.style.display = 'none';
        });

        // Expand every element that could clip content in the clone
        [clonedEl, ...clonedEl.querySelectorAll('*')].forEach((node) => {
          const cs  = window.getComputedStyle(node);
          const ovs = [cs.overflow, cs.overflowX, cs.overflowY].join('|');
          if (/hidden|auto|scroll/.test(ovs)) {
            node.style.overflow  = 'visible';
            node.style.overflowX = 'visible';
            node.style.overflowY = 'visible';
            node.style.maxHeight = 'none';
            node.style.maxWidth  = 'none';
            node.style.height    = 'auto';
          }
        });

        // Root element sizing
        clonedEl.style.width    = naturalW + 'px';
        clonedEl.style.height   = naturalH + 'px';
        clonedEl.style.overflow = 'visible';

        // ResponsiveContainers: force full natural width
        clonedEl.querySelectorAll('.recharts-responsive-container').forEach((rc) => {
          rc.style.width     = naturalW + 'px';
          rc.style.minHeight = '150px';
        });

        // SVGs: remove any clip-path constraints
        clonedEl.querySelectorAll('svg').forEach((svg) => {
          svg.style.overflow = 'visible';
          svg.removeAttribute('clip-path');
        });
      },
    });

    // ── Step 5: add padding in the final image ────────────────────────────────
    const padded = document.createElement('canvas');
    padded.width  = canvas.width  + PADDING * 4;   // PADDING*2 because scale=2
    padded.height = canvas.height + PADDING * 4;
    const ctx = padded.getContext('2d');
    ctx.fillStyle = isDark ? '#0a0f1e' : '#f8fafc';
    ctx.fillRect(0, 0, padded.width, padded.height);
    ctx.drawImage(canvas, PADDING * 2, PADDING * 2);

    padded.toBlob((blob) => {
      if (!blob) { toast.error('Failed to generate image'); return; }
      const url = URL.createObjectURL(blob);
      const a   = document.createElement('a');
      a.href     = url;
      a.download = `${filename}-${new Date().toISOString().slice(0, 10)}.png`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Chart exported!');
    }, 'image/png');
  } catch (err) {
    console.error('Chart export error:', err);
    toast.error('Export failed');
  } finally {
    setLoading(false);
  }
};

/**
 * Reusable Export button — label is "Export".
 * The data-download-btn attribute hides it in captured PNG images.
 */
export const DownloadBtn = ({ onClick, loading, isDark }) => (
  <button
    onClick={onClick}
    disabled={loading}
    data-download-btn
    title="Export as PNG"
    style={{
      display: 'flex', alignItems: 'center', gap: 5,
      padding: '4px 12px', borderRadius: 20, border: 'none',
      cursor:     loading ? 'not-allowed' : 'pointer',
      fontSize:   11, fontWeight: 600,
      background: isDark ? 'rgba(248,114,23,0.12)' : 'rgba(248,114,23,0.08)',
      color:      '#F87217',
      opacity:    loading ? 0.6 : 1,
      transition: 'background 0.2s',
      whiteSpace: 'nowrap',
    }}
    onMouseEnter={(e) => {
      if (!loading) e.currentTarget.style.background = isDark ? 'rgba(248,114,23,0.24)' : 'rgba(248,114,23,0.16)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = isDark ? 'rgba(248,114,23,0.12)' : 'rgba(248,114,23,0.08)';
    }}
  >
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
      <polyline points="7 10 12 15 17 10"/>
      <line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
    {loading ? 'Saving…' : 'Export'}
  </button>
);